import { t } from '../i18n.ts';
import type { MessageKey } from '../i18n.ts';

/**
 * The move that answers an engine refusal (standards/messages.md).
 *
 * The engine states facts: `game input rejected: no-open-scene`, `seat-taken:comms`. That is correct of
 * it - a rejection code is a machine token and the server has no business knowing that one client spells
 * the fix `/scene standup`. The join is the client's job, and this is where it happens.
 *
 * Codes are matched by PREFIX because several carry a value (`seat-taken:comms`, `kr-duplicate:kr-trust`);
 * the value is handed to the message so the line can name the thing the player actually typed.
 *
 * A code with no entry gets no invented advice. "Try again" for a refusal that will refuse again is the
 * failure this file exists to prevent - the standard is explicit that a wrong action is worse than none.
 */
const ACTIONS: readonly (readonly [string, MessageKey])[] = [
  // Scenes.
  ['no-open-scene', 'refuse.noOpenScene'],
  ['scene-already-open', 'refuse.sceneAlreadyOpen'],
  ['not-in-standup', 'refuse.notInStandup'],
  ['slot-used', 'refuse.slotUsed'],

  // Seats. `seat-taken` and `seat-occupied` are the same news from two call sites.
  ['seat-taken', 'refuse.seatTaken'],
  ['seat-occupied', 'refuse.seatTaken'],
  ['already-seated', 'refuse.alreadySeated'],
  ['not-seated', 'refuse.notSeated'],
  ['unknown-seat', 'refuse.unknownSeat'],
  ['over-capacity', 'refuse.overCapacity'],

  // The session clock.
  ['session-started', 'refuse.sessionStarted'],
  ['no-open-gate', 'refuse.noOpenGate'],

  // OKRs.
  ['kr-duplicate', 'refuse.krDuplicate'],
  ['too-many-krs', 'refuse.tooManyKrs'],
  ['unknown-kr', 'refuse.unknownKr'],
  ['kr-graded', 'refuse.krGraded'],

  // Decisions.
  ['decision-not-owner', 'refuse.decisionNotOwner'],
  ['decision-unknown-option', 'refuse.decisionUnknownOption'],
  ['decision-unknown', 'refuse.decisionUnknown'],
  ['decision-insufficient-consults', 'refuse.decisionConsults'],
  ['decision-no-quorum', 'refuse.decisionQuorum'],
  ['decision-awaiting-approval', 'refuse.decisionApproval'],

  // Work.
  ['work-unknown', 'refuse.workUnknown'],
  ['work-duplicate', 'refuse.workDuplicate'],
  ['work-not-startable', 'refuse.workNotStartable'],
  ['backlog-full', 'refuse.backlogFull'],
];

/**
 * Server-side validation sentences (not engine codes) that a player can act on. Matched on a fragment
 * rather than the whole string because the server owns its wording and may reword it - a hint that stops
 * appearing is a smaller loss than a hint that contradicts the sentence above it.
 */
const SERVER_ACTIONS: readonly (readonly [string, MessageKey])[] = [
  ['you hold no seat', 'refuse.notSeated'],
  ['not a game session', 'refuse.notAGame'],
];

/**
 * The action line for a refusal, or `null` when we have nothing true to say.
 *
 * @param message The whole refusal as printed, including the code.
 */
export function refusalAction(message: string): string | null {
  const code = /game input rejected:\s*([a-z-]+)(?::([^\s]+))?/i.exec(message);
  if (code) {
    const key = ACTIONS.find(([prefix]) => prefix === code[1])?.[1];
    if (key) {
      return t(key, { value: code[2] ?? '' });
    }
  }

  const lower = message.toLowerCase();
  const server = SERVER_ACTIONS.find(([fragment]) => lower.includes(fragment))?.[1];
  return server ? t(server, { value: '' }) : null;
}
